import { createContext, useContext, useState, ReactNode } from 'react';
import { clearAuthSnapshot, readAuthSnapshot, writeAuthSnapshot } from '../lib/authStorage';
import { getPublicUrl } from '../lib/subdomains';

type User = {
  id: number;
  email: string;
  role: 'ADMIN' | 'CLIENT' | 'EMPLOYEE' | 'PARTNER';
  org_id?: number;
  profile_role?: string;
  name?: string;
};

type AuthContextType = {
  user: User | null;
  login: (token: string, userData: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getInitialUser(): User | null {
  return readAuthSnapshot()?.user ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getInitialUser);

  const login = (token: string, userData: User) => {
    writeAuthSnapshot(token, userData);
    setUser(userData);
  };

  const logout = () => {
    clearAuthSnapshot();
    setUser(null);

    const target = getPublicUrl('/login') || '/login';
    if (typeof window !== 'undefined') {
      window.location.replace(target);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
